import React from "react";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { DealData, DealStatus } from "@/pages/deals/types";
import { LeadData, LeadStatus } from "@/pages/leads/types";
import { ContractTableRow, ContractTableStatus } from "@/pages/contracts/types";
import { contractTableStatusColors } from "@/pages/contracts/helpers";
import { getEnumTKey } from "@/utils/strings";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { dealStatusColors } from "@/pages/deals/components/DealsTable/DealsTable";
import { leadStatusColors } from "@/pages/leads/helpers";
import { ArrowUpRight, Dollar, Product, User } from "@dreamhub/designsystem/icons";
import { formatNumber } from "@/utils/numbers";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { closedLostReasonsFromIdx } from "@/pages/deals/helpers";
import { useClosedLostReasons } from "@/entities/settings/deal/hooks";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import Chip from "@/components/ui/chip";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";

interface Props {
  entity: DealData | LeadData | ContractTableRow;
  entityType: EntityType;
  detailedViewEntityType: EntityType;
}

const Association: React.FC<Props> = ({
  entity,
  entityType,
  detailedViewEntityType
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();
  const { getValue: getArrOrMrrValue, isArr } = useArrMrr();
  const closedLostReasons =
    useClosedLostReasons().data?.closedLostReasons ?? [];

  const url = `/${entityTypeToEntityName[entityType]}/${entity.id}`;

  const handleOpenDrawer = () => {
    newOpenDrawer(entity.id, entityType);
  };

  const contract = entityType === EntityType.CONTRACT ? (entity as ContractTableRow) : null;
  const contractCarrOrCmrr = contract ? (isArr ? contract.carr : contract.cmrr) : null;

  let statusColor: string | undefined;
  let statusLabel: string;
  switch (entityType) {
    case EntityType.CONTRACT: {
      const contractStatus = contract!.status;
      statusColor = contractStatus != null ? contractTableStatusColors[contractStatus] : undefined;
      statusLabel = contractStatus != null ? t(getEnumTKey("contracts.table", "statusValues", ContractTableStatus, contractStatus)) : "";
      break;
    }
    case EntityType.DEAL:
      statusColor = dealStatusColors[entity.status as DealStatus];
      statusLabel = t(getEnumTKey("stakeholders.dealsModal.table", "status", DealStatus, entity.status as DealStatus));
      break;
    default:
      statusColor = leadStatusColors[entity.status as LeadStatus];
      statusLabel = t(getEnumTKey("leads", "statusValues", LeadStatus, entity.status as LeadStatus));
  }

  return (
    <>
      <div
        data-test={`association-${entityType}-${entity.id}`}
        onClick={handleOpenDrawer}
        className="relative flex w-full group items-start cursor-pointer transition-colors duration-200 ease-in-out rounded-xl hover:bg-white-4"
      >
        <div className="w-full min-w-0 p-2 flex flex-col gap-2">
          <div className="flex gap-2 items-center min-w-0">
            <EntityLogoIconContainer
              entityType={entityType}
              className="!size-6 !rounded-md p-px"
              innerClassName="!rounded-[5px]"
              iconSize="sm"
            />
            <Text variant="label" className="truncate">
              {entity.name}
            </Text>
            <Text variant="bodySecondary" className="shrink-0">
              {`(${t(getEnumTKey("entities", "names", EntityType, entityType))})`}
            </Text>
          </div>
          <div className="flex flex-wrap items-center gap-x-2">
            <Chip
              size="lg"
              preview
              statusColor={statusColor}
              label={statusLabel}
              textVariant="bodySecondary"
              textClassName="text-text-body-secondary"
            />
            {entity?.productName && (
              <>
                <div className="w-px min-w-[1px] h-3 bg-border-border2" />
                <Chip
                  size="lg"
                  preview
                  icon={
                    <IconContainer className="!size-[18px]" color="secondary">
                      <Product />
                    </IconContainer>
                  }
                  label={entity.productName}
                  textVariant="bodySecondary"
                  textClassName="text-text-body-secondary"
                />
              </>
            )}
            {entityType === EntityType.LEAD ? (
              (() => {
                const lead = entity as LeadData;
                const leadPersonName = `${lead.contactFirstName ?? ""} ${
                  lead.contactLastName ?? ""
                }`.trim();
                return leadPersonName ? (
                  <>
                    <div className="w-px min-w-[1px] h-3 bg-border-border2" />
                    <Chip
                      size="lg"
                      preview
                      icon={
                        <IconContainer
                          className="!size-[18px]"
                          color="secondary"
                        >
                          <User />
                        </IconContainer>
                      }
                      label={leadPersonName}
                      textVariant="bodySecondary"
                      textClassName="text-text-body-secondary"
                    />
                  </>
                ) : null;
              })()
            ) : entityType === EntityType.CONTRACT ? (
              <>
                <div className="w-px min-w-[1px] h-3 bg-border-border2" />
                <Chip
                  size="lg"
                  preview
                  icon={
                    <IconContainer className="!size-[18px]" color="secondary">
                      <Dollar />
                    </IconContainer>
                  }
                  label={formatNumber(contractCarrOrCmrr ?? 0, 0, true, false).value}
                  textVariant="bodySecondary"
                  textClassName="text-text-body-secondary"
                />
                {contract?.totalContractValue != null ? (
                  <>
                    <div className="w-px min-w-[1px] h-3 bg-border-border2" />
                    <Chip
                      size="lg"
                      preview
                      label={formatNumber(contract.totalContractValue, 0, true, false).value}
                      textVariant="bodySecondary"
                      textClassName="text-text-body-secondary"
                    />
                  </>
                ) : null}
              </>
            ) : (
              <>
                <div className="w-px min-w-[1px] h-3 bg-border-border2" />
                <Chip
                  size="lg"
                  preview
                  icon={
                    <IconContainer className="!size-[18px]" color="secondary">
                      <Dollar />
                    </IconContainer>
                  }
                  label={
                    formatNumber(getArrOrMrrValue(entity) || 0, 0, true, false)
                      .value
                  }
                  textVariant="bodySecondary"
                  textClassName="text-text-body-secondary"
                />
              </>
            )}
          </div>
          {entityType === EntityType.DEAL &&
            entity?.status === DealStatus.CLOSED_LOST && (
              <>
                {entity?.closedLostReason && (
                  <Text variant="bodySecondary">
                    {closedLostReasonsFromIdx(
                      t,
                      i18n,
                      closedLostReasons,
                      entity?.closedLostReason as number
                    )}
                  </Text>
                )}
                {entity?.closedLostReasonText && (
                  <Text variant="bodySecondary">
                    {entity?.closedLostReasonText}
                  </Text>
                )}
              </>
            )}
          {entityType === EntityType.DEAL &&
            detailedViewEntityType === EntityType.PERSON &&
            (entity as DealData)?.role && (
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 rounded border border-border-border2 bg-surface-focus-selected">
                  <Text variant="smBody">{(entity as DealData)?.role}</Text>
                </div>
                <Text variant="bodySecondary">{t("involvedIn.inDeal")}</Text>
              </div>
            )}
        </div>
        <div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100">
          <Tooltip>
            <TooltipTrigger>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(url);
                }}
                variant="icon"
                size="md"
              >
                <ArrowUpRight />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent>{t("detailedViewTooltip")}</TooltipContent>
          </Tooltip>
        </div>
      </div>
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={entityType}
        onClose={newCloseDrawer}
      />
    </>
  );
};

export default Association;
